"use client"

import { useBreakpoints } from "@/components/Providers/ThemeRegistry/BreakpointsProvider"
import { colors } from "@/components/Providers/ThemeRegistry/theme"
import { ArrowBoost, EthereumIcon, MantleIcon, SolanaIcon } from "@/components/svgs/Svgs"
import { Paper, Stack, Typography, alpha } from "@mui/material"
import Box from "@/components/Box"
import Link from "next/link"

export const NetworkChip = ({ network }: { network: "ethereum" | "solana" | "mantle" }) => {
  const Icon = network === "ethereum" ? EthereumIcon : network === "solana" ? SolanaIcon : MantleIcon
  const label = network === "ethereum" ? "Ethereum" : network === "solana" ? "Solana" : "Mantle"

  const { sm } = useBreakpoints()
  const iconSize = sm.up ? "24px" : "16px"

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="6px"
      sx={{
        p: { xs: "3px", sm: "4px" },
        pr: { xs: "8px", sm: "10px" },
        background: alpha(colors.base.black, 0.05),
        borderRadius: "40px",
      }}
    >
      <Icon width={iconSize} height={iconSize} />
      <Typography variant={sm.up ? "body2" : "subtitle1"} fontWeight={500} color={alpha(colors.base.black, 0.6)}>
        {label}
      </Typography>
    </Stack>
  )
}

export const BoosterChip = ({ boosters }: { boosters: number }) => {
  const { sm } = useBreakpoints()
  const iconSize = sm.up ? "18px" : "15px"

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="6px"
      sx={{
        py: { xs: "5px", sm: "6px" },
        px: { xs: "8px", sm: "10px" },
        backgroundColor: alpha(colors.blue.main, 0.1),
        borderRadius: "100px",
      }}
    >
      <ArrowBoost width={iconSize} height={iconSize} />
      <Typography variant={sm.up ? "body2" : "subtitle1"} color={colors.blue.main} fontWeight={500}>
        {boosters} Booster{boosters > 1 ? "s" : ""}
      </Typography>
    </Stack>
  )
}

type CampaignCardProps = {
  title: string
  body: string
  cardIcon: React.ReactNode
  chips?: React.ReactNode
  button?: React.ReactNode
  href?: string
  helperText?: string
}

export const CampaignCard = ({ title, body, cardIcon, chips, button, href, helperText }: CampaignCardProps) => {
  if (!!href) {
    return (
      <Link
        href={href}
        style={{
          textDecoration: "none",
        }}
      >
        <Box
          sx={{
            cursor: "pointer",
            "&:hover": {
              outline: `3px solid ${colors.blue.main}`,
              borderColor: "transparent",
              boxShadow: "none",
            },
            borderRadius: "16px",
          }}
        >
          <CampaignCardInner
            title={title}
            body={body}
            cardIcon={cardIcon}
            chips={chips}
            button={button}
            helperText={helperText}
          />
        </Box>
      </Link>
    )
  }
  return <CampaignCardInner title={title} body={body} cardIcon={cardIcon} chips={chips} button={button} />
}

const CampaignCardInner = ({ title, body, cardIcon, chips, button, href }: CampaignCardProps) => {
  return (
    <Paper
      component={"div"}
      sx={{
        textDecoration: "none",
        padding: "32px",
        minHeight: { xs: "320px", sm: "360px" },
        border: `1px solid ${alpha(colors.base.black, 0.08)}`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Stack gap="16px" pb="24px">
        {cardIcon}
        <Typography variant="bodyL" fontWeight={800}>
          {title}
        </Typography>
        {chips && (
          <Stack direction="row" columnGap="4px" rowGap="8px" flexWrap="wrap">
            {chips}
          </Stack>
        )}
        <Typography variant="body1" fontWeight={500} sx={{ opacity: 0.6 }}>
          {body}
        </Typography>
      </Stack>
      {button && button}
    </Paper>
  )
}
